/* eslint-disable @typescript-eslint/no-explicit-any */

/**
 * Razorpay web checkout.
 *
 * Razorpay ships no npm package for the browser checkout - it is a script tag
 * that puts `Razorpay` on `window`. Loaded on demand rather than in the app
 * shell so a Cashfree booking never pays for it.
 *
 * The publishable key comes from the booking response (`razorpayKeyId`), not
 * from website env: the account it points at is server config, and baking a copy
 * in here would turn an account switch into a website release.
 */

const RAZORPAY_CHECKOUT_SRC = 'https://checkout.razorpay.com/v1/checkout.js';

let loader: Promise<boolean> | null = null;

export const loadRazorpayCheckout = (): Promise<boolean> => {
  if (typeof window === 'undefined') return Promise.resolve(false);
  if ((window as any).Razorpay) return Promise.resolve(true);
  // Cached so a retry after a failed booking does not append a second script.
  if (loader) return loader;

  loader = new Promise<boolean>((resolve) => {
    const existing = document.querySelector(
      `script[src="${RAZORPAY_CHECKOUT_SRC}"]`,
    );
    if (existing) {
      existing.addEventListener('load', () => resolve(true));
      existing.addEventListener('error', () => resolve(false));
      return;
    }

    const script = document.createElement('script');
    script.src = RAZORPAY_CHECKOUT_SRC;
    script.async = true;
    script.onload = () => resolve(true);
    script.onerror = () => {
      // Allow a later attempt to re-add the script.
      loader = null;
      resolve(false);
    };
    document.body.appendChild(script);
  });

  return loader;
};

export interface TRazorpayCheckoutArgs {
  keyId: string;
  orderId: string;
  /** Rupees. Razorpay expects paise, so this is converted here. */
  amount: number;
  name?: string;
  description?: string;
  prefill?: { name?: string; email?: string; contact?: string };
  /**
   * Which payment methods the modal offers - `{ upi: true, emi: false, ... }`.
   *
   * Set in admin and returned by the booking response, never decided here: the
   * rules are business config (a 159-rupee ticket has no use for EMI, a concert
   * ticket does) and baking them in would turn every change into a website
   * release. Undefined leaves the Razorpay account defaults alone, so a backend
   * that does not send the field behaves exactly as before.
   */
  method?: Record<string, boolean>;
  notes?: Record<string, string>;
  onSuccess: (response: any) => void;
  /** Called for an explicit failure. */
  onFailure: (message: string) => void;
  /** Called when the user closes the modal without paying. */
  onDismiss: () => void;
}

/**
 * Opens Razorpay checkout for an order the backend already created.
 *
 * Success here means "the gateway accepted the payment", not "the booking is
 * confirmed" - confirmation happens server-side off the payment.captured
 * webhook. Callers should route to the payment-status screen and let it poll,
 * exactly as the Cashfree path does, rather than treating this callback as
 * proof the ticket exists.
 */
export const openRazorpayCheckout = async ({
  keyId,
  orderId,
  amount,
  name,
  description,
  prefill,
  method,
  notes,
  onSuccess,
  onFailure,
  onDismiss,
}: TRazorpayCheckoutArgs) => {
  const ready = await loadRazorpayCheckout();
  if (!ready || !(window as any).Razorpay) {
    onFailure('Could not load the payment gateway. Please try again.');
    return;
  }

  if (!keyId || !orderId) {
    onFailure('Payment could not be started. Please try again.');
    return;
  }

  const razorpay = new (window as any).Razorpay({
    key: keyId,
    order_id: orderId,
    amount: Math.round(amount * 100),
    currency: 'INR',
    name: name || 'Fydo',
    description,
    prefill,
    // Spread so an absent map leaves the key off entirely - passing
    // `method: undefined` is not the same to Razorpay as not passing it.
    ...(method ? { method } : {}),
    notes,
    handler: (response: any) => onSuccess(response),
    modal: {
      ondismiss: () => onDismiss(),
    },
  });

  razorpay.on('payment.failed', (response: any) => {
    onFailure(
      response?.error?.description || 'Payment failed. Please try again.',
    );
  });

  razorpay.open();
};
